/**
 * PsychRadar — lightweight SVG radar chart showing the 5 psychological
 * dimensions computed by the frontend `buildProfile` and echoed on the
 * consultation as `reading.profile_dimensions` and `reading.profile_labels`.
 *
 * Zero external dependencies. Renders 5 axes, a gold-filled polygon for the
 * user's scores, gridlines at 20/40/60/80/100, subtle labels, and (optionally)
 * the one-word inner-state per axis under each point.
 */
export default function PsychRadar({
  dimensions = {},
  labels = {},
  axisNames,
  size = 320,
  className = "",
  showLabels = true,
}) {
  const axes = [
    { key: "decision_confidence", short: axisNames?.decision_confidence || "Decision Confidence" },
    { key: "emotional_clarity",   short: axisNames?.emotional_clarity   || "Emotional Clarity" },
    { key: "risk_readiness",      short: axisNames?.risk_readiness      || "Risk Readiness" },
    { key: "purpose_alignment",   short: axisNames?.purpose_alignment   || "Purpose Alignment" },
    { key: "self_trust",          short: axisNames?.self_trust          || "Self-Trust" },
  ];

  const cx = size / 2;
  const cy = size / 2;
  // Reserve outer band for axis labels so they don't collide with the chart.
  const radius = size / 2 - 62;

  // 5 evenly spaced axes, starting from top (‑90°).
  const angleFor = (i) => (-Math.PI / 2) + (i * (2 * Math.PI)) / axes.length;

  // Convert a 0-100 score → (x,y) on that axis.
  const point = (i, value) => {
    const clamped = Math.max(0, Math.min(100, Number(value) || 0));
    const r = (clamped / 100) * radius;
    const a = angleFor(i);
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)];
  };

  // Full axis endpoint (used for grid + axis lines).
  const axisEnd = (i) => point(i, 100);

  // Gridline polygons at 20/40/60/80/100 %.
  const gridLevels = [20, 40, 60, 80, 100];

  // User polygon vertices.
  const userPts = axes.map((ax, i) => point(i, dimensions[ax.key]));
  const userPath = userPts.map(([x, y]) => `${x},${y}`).join(" ");

  return (
    <svg
      viewBox={`0 0 ${size} ${size}`}
      width="100%"
      height="100%"
      className={className}
      aria-label="Psychological profile radar chart"
      data-testid="psych-radar"
    >
      <defs>
        <radialGradient id="radar-fill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="rgba(212,175,55,0.55)" />
          <stop offset="100%" stopColor="rgba(212,175,55,0.10)" />
        </radialGradient>
        <linearGradient id="radar-stroke" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FBE38E" />
          <stop offset="100%" stopColor="#D4AF37" />
        </linearGradient>
      </defs>

      {/* Grid pentagons */}
      {gridLevels.map((lvl) => {
        const pts = axes
          .map((_, i) => point(i, lvl))
          .map(([x, y]) => `${x},${y}`)
          .join(" ");
        return (
          <polygon
            key={`grid-${lvl}`}
            points={pts}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="0.75"
          />
        );
      })}

      {/* Axes */}
      {axes.map((ax, i) => {
        const [ex, ey] = axisEnd(i);
        return (
          <line
            key={`axis-${ax.key}`}
            x1={cx}
            y1={cy}
            x2={ex}
            y2={ey}
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="0.75"
          />
        );
      })}

      {/* User polygon */}
      <polygon
        points={userPath}
        fill="url(#radar-fill)"
        stroke="url(#radar-stroke)"
        strokeWidth="1.75"
        style={{ filter: "drop-shadow(0 0 8px rgba(212,175,55,0.25))" }}
      />

      {/* User vertex dots */}
      {userPts.map(([x, y], i) => (
        <circle
          key={`dot-${axes[i].key}`}
          cx={x}
          cy={y}
          r="3.5"
          fill="#D4AF37"
          stroke="#0B1020"
          strokeWidth="1.25"
        />
      ))}

      {/* Axis labels — outer edge, offset to avoid overlap */}
      {showLabels &&
        axes.map((ax, i) => {
          const a = angleFor(i);
          const labelR = radius + 26;
          const lx = cx + labelR * Math.cos(a);
          const ly = cy + labelR * Math.sin(a);
          const score = Math.round(Number(dimensions[ax.key]) || 0);
          const anchor =
            Math.cos(a) > 0.3 ? "start" : Math.cos(a) < -0.3 ? "end" : "middle";
          const inner = labels[ax.key];
          return (
            <g key={`lab-${ax.key}`}>
              <text
                x={lx}
                y={ly - 4}
                textAnchor={anchor}
                fill="rgba(226,232,240,0.85)"
                fontSize="10"
                letterSpacing="0.25em"
                fontFamily='"Inter", system-ui'
                style={{ textTransform: "uppercase" }}
              >
                {ax.short}
              </text>
              <text
                x={lx}
                y={ly + 12}
                textAnchor={anchor}
                fill="#D4AF37"
                fontSize="14"
                fontFamily='"Cormorant Garamond", serif'
                fontStyle="italic"
              >
                {score}
                <tspan
                  fill="rgba(226,232,240,0.5)"
                  fontSize="10"
                  fontFamily='"Inter", system-ui'
                  fontStyle="normal"
                >
                  /100
                </tspan>
              </text>
              {inner && (
                <text
                  x={lx}
                  y={ly + 26}
                  textAnchor={anchor}
                  fill="rgba(226,232,240,0.55)"
                  fontSize="9"
                  fontFamily='"Inter", system-ui'
                  fontStyle="italic"
                >
                  {String(inner).replaceAll("-", " ")}
                </text>
              )}
            </g>
          );
        })}
    </svg>
  );
}
